import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { EditPencil } from "components/icons/EditPencil";
import { ReactNode } from "react";
import { StepBadge } from "./StepBadge";
import { StepRail } from "./StepRail";
import { BadgeKind, StepStatus, StepSummary } from "./types";

type StepCardProps = {
  number: number;
  title: string;
  subtitle: string;
  badge: BadgeKind;
  status: StepStatus;
  isLast: boolean;
  summary?: StepSummary;
  onEdit?: () => void;
  /** Body is built only when the step is active, so inactive steps mount nothing. */
  renderBody: () => ReactNode;
};

export const StepCard = ({
  number,
  title,
  subtitle,
  badge,
  status,
  isLast,
  summary,
  onEdit,
  renderBody,
}: StepCardProps) => {
  const isActive = status === "active";
  const isDone = status === "done";
  const isLocked = status === "locked";

  return (
    <div className="t-flex t-items-stretch t-group">
      <StepRail number={number} status={status} isLast={isLast} />
      <div
        className={classNames(
          "group-[&:not(:last-child)]:t-mb-8 t-ml-5 t-flex-1 sm:t-ml-0 t-overflow-hidden t-rounded-[18px] t-border t-border-solid t-transition-all t-duration-300",
          {
            "t-border-purple-30 t-bg-white t-shadow-light-60": isActive,
            "t-border-neutral-10 t-bg-neutral-66": isDone,
            "t-border-neutral-10 t-bg-white t-opacity-40": isLocked,
          }
        )}
      >
        <div className="t-flex t-items-center t-gap-3 t-px-6 t-py-5 sm:t-px-4 sm:t-py-4">
          <div className="t-min-w-0 t-flex-1">
            <div className="t-text-subtitle t-text-text-60 t-font-normal">
              {title}
            </div>
            <div className="t-mt-0.5 t-text-body-sm t-text-neutral-30">
              {subtitle}
            </div>
          </div>
          <StepBadge kind={badge} status={status} />
        </div>

        {isDone && summary !== undefined && (
          <div className="t-flex t-items-center t-gap-3 t-px-6 t-pb-5 sm:t-px-4 sm:t-pb-4 t-text-body t-text-text-60">
            <div className="t-min-w-0">{summary}</div>
            {onEdit && (
              <Button
                customType="link"
                size="small"
                type="button"
                leftIcon={<EditPencil />}
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
          </div>
        )}

        {isActive && (
          <>
            <div className="t-h-px t-bg-neutral-0 t-mx-6 sm:t-mx-4" />
            <div className="t-p-6 sm:t-p-4">{renderBody()}</div>
          </>
        )}
      </div>
    </div>
  );
};
